import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { clsx } from 'clsx';
import { getQTipData } from '../Tooltip/qTip.utilities';
import { InsertSeeqContent, SeeqActionDropdownItem, ViewWorkbench } from './variants';
import type { QTipDataAttributes } from '../Tooltip/QTip.types';
import type { SeeqActionDropdownItems, SeeqActionDropdownProps } from './SeeqActionDropdown.types';

const borderClassNames = `
  tw:rounded-md
  tw:border
  tw:border-solid
  tw:border-sq-disabled-gray
  tw:dark:border-gray-500
`;

const backgroundClassNames = `
  tw:bg-sq-white
  tw:dark:bg-sq-dark-background
`;

const renderItem = (variant: SeeqActionDropdownProps['variant'], item: SeeqActionDropdownItems) => {
  switch (variant) {
    case 'create-workbench':
      return <SeeqActionDropdownItem {...item} />;
    case 'view-workbench':
      return <ViewWorkbench {...item} />;
    case 'insert-seeq-content':
      return <InsertSeeqContent {...item} />;
    default:
      return <SeeqActionDropdownItem {...item} />;
  }
};

const SeeqActionDropdown = ({
  seeqActionDropdownItems,
  trigger,
  id,
  extraClassNames,
  containerTestId,
  disabled = false,
  align = 'end',
  placement = 'bottom',
  placementOffset = 5,
  alignOffset = -35,
  hasArrow = false,
  onOpenChange,
  isOpen,
  setFocusOnTriggerOnClose = true,
  keepFocusInsideDropdown = true,
  variant,
  ...tooltipProps
}: SeeqActionDropdownProps) => {
  const tooltipData: QTipDataAttributes | undefined = getQTipData(tooltipProps);

  return (
    <DropdownMenu.Root defaultOpen={false} open={isOpen} onOpenChange={onOpenChange} modal={keepFocusInsideDropdown}>
      <DropdownMenu.Trigger
        id={id}
        data-testid={id}
        className="tw:border-none tw:focus-within:outline-none tw:focus:outline-none tw:focus-visible:outline-none"
        asChild
        disabled={disabled}>
        <div
          {...tooltipData}
          className={clsx(
            `tw:flex tw:flex-col tw:items-center tw:bg-transparent
            tw:focus-within:outline-none
            tw:focus:outline-none
            tw:focus-visible:outline-none`,
            {
              'tw:cursor-not-allowed tw:opacity-50': disabled,
            },
            extraClassNames,
          )}>
          {trigger}
        </div>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content
        sideOffset={placementOffset}
        side={placement}
        align={align}
        alignOffset={alignOffset}
        asChild
        onCloseAutoFocus={(e) => !setFocusOnTriggerOnClose && e.preventDefault()}
        className="tw:outline-none tw:focus-visible:outline-none">
        <div
          data-testid={containerTestId}
          className={clsx(
            `tw:relative tw:z-1200 tw:min-w-6 tw:py-2
             tw:shadow-[0_4px_8px_rgba(0,0,0,0.2)] tw:outline-none
             tw:focus-visible:outline-none
             tw:data-[side=bottom]:animate-slideUpAndFade
             tw:data-[side=left]:animate-slideRightAndFade
             tw:data-[side=right]:animate-slideLeftAndFade
             tw:data-[side=top]:animate-slideDownAndFade`,
            borderClassNames,
            backgroundClassNames,
          )}>
          {hasArrow && (
            <DropdownMenu.Arrow asChild>
              <div
                className="
                  tw:-mt-sq-7 tw:h-sq-15 tw:w-sq-15 tw:rotate-45 tw:border-r
                  tw:border-b tw:border-sq-disabled-gray tw:bg-sq-white
                  tw:fill-transparent
                  tw:dark:border-gray-500 tw:dark:bg-sq-dark-background
                "
              />
            </DropdownMenu.Arrow>
          )}
          {seeqActionDropdownItems.map((item, index) => {
            return (
              <div key={item.id ?? index}>
                <DropdownMenu.Item
                  onSelect={(e) => {
                    item.action(e);
                  }}
                  className="tw:relative tw:flex tw:cursor-pointer tw:outline-none tw:select-none tw:hover:bg-sq-gray-highlight tw:aria-disabled:pointer-events-none tw:data-dim:opacity-50 tw:data-disabled:text-sq-disabled-gray tw:dark:text-sq-white tw:dark:hover:bg-sq-gray-highlight-dark"
                  data-testid={item.testId}
                  data-dim={item.dim ? '' : undefined}
                  disabled={!item.enabled}>
                  {renderItem(variant, item)}
                </DropdownMenu.Item>
                {item.divider && (
                  <DropdownMenu.Separator
                    data-testid={`dropdown-divider-${index}`}
                    className="tw:my-2 tw:h-px tw:bg-sq-disabled-gray tw:dark:bg-gray-500"
                  />
                )}
              </div>
            );
          })}
        </div>
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
};

export default SeeqActionDropdown;
